import { useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { faBan, faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import type { TemplatePageCategoriesFilters } from '@hooks/useTemplatePageCategories'
import useCurrentUser from '@hooks/useCurrentUser'
import useTemplatePageCategories from '@hooks/useTemplatePageCategories'
import { generateUrls } from '@hooks/useTemplatePageCategory'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import TemplatePageCategoriesTable from '../TemplatePageCategoriesTable'

const buildPageFilterFields = (currentUserPayload: ReturnType<typeof useCurrentUser>) => {
  const { isBrandUser } = currentUserPayload

  const boxProps = { marginBottom: 'large' }
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'global',
      label: 'Global',
      values: [
        { key: 'true', label: 'True' },
        { key: 'false', label: 'False' },
      ],
    },
    {
      callbacks: {
        shouldRender: ({ global }: { global?: string }) => global !== 'true',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      hidden: isBrandUser,
      key: 'brand_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: TemplatePageCategoriesFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const currentUserPayload = useCurrentUser()

  const { hidden }: { hidden?: 'true' | 'false' } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden || 'false'

  const { templatePageCategoriesIndexUrl, hiddenTemplatePageCategoriesIndexUrl } = generateUrls()

  const templatePageCategoriesPayload = useTemplatePageCategories({
    filters: {
      hidden: isHidden,
      ...pageFilters,
    },
    performHttpRequests: true,
    requestOptions: defaultRequestOptions.templatePageCategory,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTemplatePageCategories,
    filteredTemplatePageCategoriesCount,
    hasFilteredTemplatePageCategories,
    loading,
  } = templatePageCategoriesPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateOrEditTemplatePageCategoryModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  return (
    <>
      <Helmet>
        <title>Template Categories | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            onClick={() => showCreateOrEditTemplatePageCategoryModal({ tag: { global: true } })}
            size="medium"
          >
            Create
          </Button>
        )}
        activeTabBarItemKey={isHidden}
        tabBarItems={[
          {
            href: templatePageCategoriesIndexUrl,
            icon: faCheckCircle,
            key: 'false',
            title: 'Current',
          },
          {
            href: hiddenTemplatePageCategoriesIndexUrl,
            icon: faBan,
            key: 'true',
            title: 'Archived',
          },
        ]}
        title="Template Categories"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredTemplatePageCategories && <ListBlankState />}

            {!loading && hasFilteredTemplatePageCategories && (
              <DashboardModule title="Template Categories">
                <Box flexDirection="column">
                  <TemplatePageCategoriesTable templatePageCategories={filteredTemplatePageCategories} />

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredTemplatePageCategoriesCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(currentUserPayload)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
